import {createLayerDailyHousingMatcher} from './layerHousing.js';
import {pigSlug} from './pig.js';

// ── the shapes this reconstruction reads ────────────────────────────────────
//
// Every count here is rebuilt from evidence rather than read off a live column,
// and the evidence comes from five different places: app_store planner JSON
// (broiler batches, pig feeder groups + breeders), the layer tables, the daily
// report tables, and the cattle/sheep directories with their transfer ledgers.
// The planner JSON is authored by forms, so its numbers arrive as either a
// number or a string — hence the parseFloat everywhere, and the index signature
// on each type: these helpers read a handful of alias spellings (hatchDate /
// hatch_date, purchase_date / purchaseDate) off rows they do not own.

export type AnimalHistoryDateInput = string | number | Date | null | undefined;

export type BroilerBatchRow = {
  name?: string | null;
  status?: string | null;
  deleted_at?: string | null;
  hatchDate?: string | null;
  hatch_date?: string | null;
  brooderIn?: string | null;
  brooder_in?: string | null;
  arrival_date?: string | null;
  processingDate?: string | null;
  processing_date?: string | null;
  birdCount?: number | string | null;
  birdCountActual?: number | string | null;
  original_count?: number | string | null;
  mortalityCumulative?: number | string | null;
  [key: string]: unknown;
};

export type DailyReportRow = {
  date?: string | null;
  batch_label?: string | null;
  batch_id?: string | null;
  mortality_count?: number | string | null;
  layer_count?: number | string | null;
  pig_count?: number | string | null;
  [column: string]: unknown;
};

export type LayerBatchRow = {
  id?: string | null;
  status?: string | null;
  deleted_at?: string | null;
  brooder_entry_date?: string | null;
  arrival_date?: string | null;
  start_date?: string | null;
  original_count?: number | string | null;
  [column: string]: unknown;
};

export type LayerHousingRow = {
  batch_id?: string | null;
  status?: string | null;
  deleted_at?: string | null;
  start_date?: string | null;
  retired_date?: string | null;
  current_count?: number | string | null;
  current_count_date?: string | null;
  [column: string]: unknown;
};

export type PigSubBatchRow = {
  id?: string | null;
  name?: string | null;
  status?: string | null;
  startDate?: string | null;
  giltCount?: number | string | null;
  boarCount?: number | string | null;
  originalPigCount?: number | string | null;
  [key: string]: unknown;
};

export type PigProcessingTripRow = {
  date?: string | null;
  pigCount?: number | string | null;
  subAttributions?:
    | readonly {subId?: string | null; subBatchName?: string | null; count?: number | string | null}[]
    | null;
  [key: string]: unknown;
};

export type PigMortalityRow = {
  date?: string | null;
  created_at?: string | null;
  count?: number | string | null;
  sub_batch_id?: string | null;
  sub_batch_name?: string | null;
  [key: string]: unknown;
};

export type PigFeederGroupRow = {
  batchName?: string | null;
  status?: string | null;
  startDate?: string | null;
  farmBorn?: boolean | null;
  giltCount?: number | string | null;
  boarCount?: number | string | null;
  originalPigCount?: number | string | null;
  subBatches?: readonly PigSubBatchRow[] | null;
  processingTrips?: readonly PigProcessingTripRow[] | null;
  pigMortalities?: readonly PigMortalityRow[] | null;
  [key: string]: unknown;
};

export type PigBreederRow = {
  sex?: string | null;
  status?: string | null;
  archived?: boolean | null;
  purchaseDate?: string | null;
  purchase_date?: string | null;
  created_at?: string | null;
  birthDate?: string | null;
  transferredFromBatch?: {
    batchName?: string | null;
    subBatchName?: string | null;
    transferDate?: string | null;
    created_at?: string | null;
  } | null;
  [key: string]: unknown;
};

// A cattle or sheep directory row. The two are one type because herdCountAt
// treats them identically, addressing the species-specific columns through the
// idField/groupField/fromField it is handed.
export type HerdAnimalRow = {
  id?: string | null;
  deleted_at?: string | null;
  purchase_date?: string | null;
  purchaseDate?: string | null;
  created_at?: string | null;
  birth_date?: string | null;
  birthDate?: string | null;
  sale_date?: string | null;
  sold_date?: string | null;
  death_date?: string | null;
  herd?: string | null;
  flock?: string | null;
  [column: string]: unknown;
};

export type HerdTransferRow = {
  cattle_id?: string | null;
  sheep_id?: string | null;
  from_herd?: string | null;
  from_flock?: string | null;
  transferred_at?: string | null;
  created_at?: string | null;
  [column: string]: unknown;
};

// Everything the snapshot builders draw on. Every key is optional: a surface
// that only charts poultry passes only the poultry sources, and each helper
// falls back to an empty list.
export type AnimalHistoryData = {
  batches?: readonly BroilerBatchRow[] | null;
  broilerDailys?: readonly DailyReportRow[] | null;
  layerBatches?: readonly LayerBatchRow[] | null;
  layerHousings?: readonly LayerHousingRow[] | null;
  layerDailys?: readonly DailyReportRow[] | null;
  feederGroups?: readonly PigFeederGroupRow[] | null;
  breeders?: readonly PigBreederRow[] | null;
  pigDailys?: readonly DailyReportRow[] | null;
  cattle?: readonly HerdAnimalRow[] | null;
  cattleTransfers?: readonly HerdTransferRow[] | null;
  sheep?: readonly HerdAnimalRow[] | null;
  sheepTransfers?: readonly HerdTransferRow[] | null;
};

// One month's reconstructed counts. The layer* freshness pair is disclosure
// data, never an input to extrapolation (see layersFreshnessAt).
export type AnimalHistorySnapshot = {
  month: string | null;
  snapshotDate: string;
  isPartialMonth: boolean;
  broilers: number;
  // True while the broiler figure counts ordered birds for a batch with no
  // day-1 count yet (constitution Q50 'est.').
  broilersEstimated: boolean;
  layers: number;
  layersOldestReported: string | null;
  layersHasUndatedCounts: boolean;
  pigs: number;
  cattle: number;
  sheep: number;
};

// A dated count contributing to a layer total. `date` is null when the evidence
// carries no report date at all.
type LayerEvidenceEntry = {count: number; date: string | null};

// One feeder-pig cohort to reconstruct: a sub-batch when the group has them,
// otherwise the group itself.
type FeederTarget = {
  group: PigFeederGroupRow;
  sub: PigSubBatchRow | null;
  subId: string | null;
  label: string | null | undefined;
  start: string | null;
  status: string | null | undefined;
  started: number;
};

export const ANIMAL_HISTORY_SPECIES = Object.freeze([
  {key: 'broilers', label: 'Broilers', cssVar: '--c-broiler'},
  {key: 'layers', label: 'Layer Hens', cssVar: '--c-layer'},
  {key: 'pigs', label: 'Pigs', cssVar: '--c-pig'},
  {key: 'cattle', label: 'Cattle', cssVar: '--c-cattle'},
  {key: 'sheep', label: 'Sheep', cssVar: '--c-sheep'},
]);

export const ACTIVE_CATTLE_HERDS = Object.freeze(['mommas', 'backgrounders', 'finishers', 'bulls']);
export const ACTIVE_SHEEP_FLOCKS = Object.freeze(['rams', 'ewes', 'feeders']);
export const ANIMAL_HISTORY_START_MONTH = '2024-10';

const ACTIVE_CATTLE_SET = new Set(ACTIVE_CATTLE_HERDS);
const ACTIVE_SHEEP_SET = new Set(ACTIVE_SHEEP_FLOCKS);
const OUTCOME_GROUPS = new Set(['processed', 'deceased', 'sold']);

function isoDate(value: AnimalHistoryDateInput): string | null {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString().slice(0, 10);
  const match = String(value).match(/\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : null;
}

// parseFloat stringifies its argument, so String() only names a coercion these
// number-or-string planner values already went through.
function num(value: unknown): number {
  const n = parseFloat(String(value));
  return Number.isFinite(n) ? n : 0;
}

function int(value: unknown): number {
  return Math.trunc(num(value));
}

function norm(value: unknown): string {
  return String(value || '')
    .toLowerCase()
    .trim();
}

function normLabel(value: unknown): string {
  return norm(value)
    .replace(/^\(processed\)\s*/, '')
    .trim();
}

function minISO(dates: readonly AnimalHistoryDateInput[]): string | null {
  const clean = dates
    .map(isoDate)
    .filter((d): d is string => Boolean(d))
    .sort();
  return clean[0] || null;
}

function maxISO(dates: readonly AnimalHistoryDateInput[]): string | null {
  const clean = dates
    .map(isoDate)
    .filter((d): d is string => Boolean(d))
    .sort();
  return clean[clean.length - 1] || null;
}

function monthKey(value: AnimalHistoryDateInput): string | null {
  const iso = isoDate(value);
  return iso ? iso.slice(0, 7) : null;
}

function monthEnd(month: string | null | undefined): string | null {
  if (!month || !/^\d{4}-\d{2}$/.test(String(month))) return null;
  const [y, m] = month.split('-').map((v) => parseInt(v, 10));
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

function addMonth(month: string): string {
  const [y, m] = month.split('-').map((v) => parseInt(v, 10));
  const d = new Date(Date.UTC(y, m, 1));
  return d.toISOString().slice(0, 7);
}

function monthsBetween(startMonth: string | null, endMonth: string | null): string[] {
  if (!startMonth || !endMonth || startMonth > endMonth) return [];
  const out: string[] = [];
  let m = startMonth;
  while (m <= endMonth) {
    out.push(m);
    m = addMonth(m);
  }
  return out;
}

function snapshotDateForMonth(month: string, asOfDate: AnimalHistoryDateInput): string {
  const end = monthEnd(month);
  const today = isoDate(asOfDate) || new Date().toISOString().slice(0, 10);
  if (!end) return today;
  return end > today ? today : end;
}

function groupById(
  rows: readonly HerdTransferRow[] | null | undefined,
  idField: 'cattle_id' | 'sheep_id',
): Map<string, HerdTransferRow[]> {
  const map = new Map<string, HerdTransferRow[]>();
  for (const row of rows || []) {
    const id = row && row[idField];
    if (!id) continue;
    let bucket = map.get(id);
    if (!bucket) {
      bucket = [];
      map.set(id, bucket);
    }
    bucket.push(row);
  }
  for (const list of map.values()) {
    list.sort((a, b) =>
      (isoDate(a.transferred_at || a.created_at) || '').localeCompare(isoDate(b.transferred_at || b.created_at) || ''),
    );
  }
  return map;
}

function batchLabels(batch: BroilerBatchRow | null | undefined): Set<string> {
  return new Set([batch && batch.name, batch && batch.id].map(normLabel).filter(Boolean));
}

function dailyMatchesLabels(daily: DailyReportRow | null | undefined, labels: ReadonlySet<string>): boolean {
  const direct = normLabel(daily && daily.batch_label);
  if (direct && labels.has(direct)) return true;
  const batchId = normLabel(daily && daily.batch_id);
  return !!batchId && labels.has(batchId);
}

export function broilersOnFarmAt(
  batches: readonly BroilerBatchRow[] | null | undefined,
  broilerDailys: readonly DailyReportRow[] | null | undefined,
  asOfDate: AnimalHistoryDateInput,
): number {
  return broilersOnFarmDetailAt(batches, broilerDailys, asOfDate).count;
}

// broilersOnFarmAt plus whether the count is an estimate: true when any batch
// it counts has no day-1 count yet and so contributes its ordered birds. The
// UI marks that figure 'est.' (constitution Q50).
export function broilersOnFarmDetailAt(
  batches: readonly BroilerBatchRow[] | null | undefined,
  broilerDailys: readonly DailyReportRow[] | null | undefined,
  asOfDate: AnimalHistoryDateInput,
): {count: number; estimated: boolean} {
  const asOf = isoDate(asOfDate);
  if (!asOf) return {count: 0, estimated: false};
  let total = 0;
  let estimated = false;
  for (const batch of batches || []) {
    if (!batch || batch.deleted_at) continue;
    const start = isoDate(
      batch.hatchDate || batch.hatch_date || batch.brooderIn || batch.brooder_in || batch.arrival_date,
    );
    if (!start || start > asOf) continue;
    const processed = isoDate(batch.processingDate || batch.processing_date);
    if (processed && processed <= asOf) continue;
    if (!processed && norm(batch.status) === 'processed') continue;

    const labels = batchLabels(batch);
    const started = int(batch.birdCountActual) || int(batch.birdCount) || int(batch.original_count);
    if (started <= 0) continue;

    const matchingDailys = (broilerDailys || []).filter((d) => {
      const date = isoDate(d && d.date);
      // Same three tests, with the parsed date bound once so the comparison has
      // a definite operand instead of re-parsing.
      return Boolean(d) && date != null && date <= asOf && dailyMatchesLabels(d, labels);
    });
    const mortality =
      matchingDailys.length > 0 || !/^b-24-/i.test(String(batch.name || ''))
        ? matchingDailys.reduce((sum, d) => sum + int(d.mortality_count), 0)
        : int(batch.mortalityCumulative);
    total += Math.max(0, started - mortality);
    if (int(batch.birdCountActual) <= 0) estimated = true;
  }
  return {count: total, estimated};
}

// Housing ↔ daily matching is the shared canonical rule from layerHousing.js:
// exact housing_name wins, a sibling housing's label never crosses over, and
// batch_id fallback applies only to a batch with exactly one housing.

type HousingMatcher = (daily: DailyReportRow, housing: LayerHousingRow) => boolean;

function layerHousingStartDate(
  housing: LayerHousingRow,
  parentBatch: LayerBatchRow | null,
  layerDailys: readonly DailyReportRow[] | null | undefined,
  matchesHousing: HousingMatcher,
): string | null {
  const declared = isoDate(
    housing.start_date || (parentBatch && (parentBatch.brooder_entry_date || parentBatch.arrival_date)),
  );
  if (declared) return declared;
  // No declared start date: derive one from the earliest dated housing-specific
  // count evidence so a real active housing does not vanish from the totals.
  const evidence: string[] = [];
  for (const d of layerDailys || []) {
    const date = isoDate(d && d.date);
    if (date && int(d.layer_count) > 0 && matchesHousing(d, housing)) evidence.push(date);
  }
  const anchorDate = isoDate(housing.current_count_date);
  if (anchorDate && int(housing.current_count) > 0) evidence.push(anchorDate);
  return minISO(evidence);
}

// Count + the date that count was actually recorded. Layer figures are
// "latest recorded" evidence, never a verified live inventory, so every
// consumer can disclose how fresh the underlying report is.
function layerHousingEvidenceAt(
  housing: LayerHousingRow,
  start: string | null,
  layerDailys: readonly DailyReportRow[] | null | undefined,
  asOf: string,
  matchesHousing: HousingMatcher,
): LayerEvidenceEntry {
  const retired = isoDate(housing.retired_date);
  const latestDaily = (layerDailys || [])
    .filter((d) => {
      const date = isoDate(d && d.date);
      if (!date || date > asOf) return false;
      if (start && date < start) return false;
      if (retired && date >= retired) return false;
      return int(d.layer_count) > 0 && matchesHousing(d, housing);
    })
    .sort((a, b) => (isoDate(b.date) || '').localeCompare(isoDate(a.date) || ''))[0];

  const anchorDate = isoDate(housing.current_count_date);
  const anchorEligible = housing.current_count != null && (!anchorDate || anchorDate <= asOf);
  if (latestDaily) {
    // The filter above already rejected any row whose date does not parse, so
    // this re-parse always succeeds; binding it once gives the comparison a
    // definite operand, and `!= null` reproduces the false that `anchorDate >
    // null` produced for the impossible case.
    const latestDailyDate = isoDate(latestDaily.date);
    // Freshest housing-specific evidence wins: a positive current_count anchor
    // dated after the latest matching daily supersedes it. An undated or zero
    // anchor never overrides dated daily evidence.
    if (
      anchorEligible &&
      anchorDate &&
      latestDailyDate != null &&
      anchorDate > latestDailyDate &&
      int(housing.current_count) > 0
    )
      return {count: int(housing.current_count), date: anchorDate};
    return {count: int(latestDaily.layer_count), date: latestDailyDate};
  }
  if (anchorEligible) return {count: Math.max(0, int(housing.current_count)), date: anchorDate || null};
  return {count: 0, date: null};
}

function layerEvidenceEntries(
  layerBatches: readonly LayerBatchRow[] | null | undefined,
  layerHousings: readonly LayerHousingRow[] | null | undefined,
  layerDailys: readonly DailyReportRow[] | null | undefined,
  asOfDate: AnimalHistoryDateInput,
): LayerEvidenceEntry[] {
  const asOf = isoDate(asOfDate);
  if (!asOf) return [];
  const batchesById = new Map(
    (layerBatches || []).map((b): [string | null | undefined, LayerBatchRow] => [b && b.id, b]),
  );
  const housingsByBatch = new Map<string, LayerHousingRow[]>();
  for (const h of layerHousings || []) {
    if (!h || !h.batch_id) continue;
    let bucket = housingsByBatch.get(h.batch_id);
    if (!bucket) {
      bucket = [];
      housingsByBatch.set(h.batch_id, bucket);
    }
    bucket.push(h);
  }
  const matchesHousing: HousingMatcher = createLayerDailyHousingMatcher(layerHousings);

  const entries: LayerEvidenceEntry[] = [];
  for (const housing of layerHousings || []) {
    if (!housing || housing.deleted_at) continue;
    const parent = batchesById.get(housing.batch_id) || null;
    const start = layerHousingStartDate(housing, parent, layerDailys, matchesHousing);
    if (!start || start > asOf) continue;
    const retired = isoDate(housing.retired_date);
    if (retired && retired <= asOf) continue;
    if (!retired && norm(housing.status) === 'retired') continue;
    entries.push(layerHousingEvidenceAt(housing, start, layerDailys, asOf, matchesHousing));
  }

  for (const batch of layerBatches || []) {
    if (!batch || batch.deleted_at || (batch.id != null && housingsByBatch.has(batch.id))) continue;
    const start = isoDate(batch.brooder_entry_date || batch.arrival_date || batch.start_date);
    if (!start || start > asOf) continue;
    if (norm(batch.status) === 'retired') continue;
    // Batch-level started counts carry no report date.
    entries.push({count: Math.max(0, int(batch.original_count)), date: null});
  }

  return entries;
}

export function layersOnFarmAt(
  layerBatches: readonly LayerBatchRow[] | null | undefined,
  layerHousings: readonly LayerHousingRow[] | null | undefined,
  layerDailys: readonly DailyReportRow[] | null | undefined,
  asOfDate: AnimalHistoryDateInput,
): number {
  return layerEvidenceEntries(layerBatches, layerHousings, layerDailys, asOfDate).reduce((sum, e) => sum + e.count, 0);
}

// Freshness of the layer figure at asOf. A combined layer total can mix
// counts reported on different dates, so the honest disclosure is the OLDEST
// dated evidence contributing to the displayed positive total (the newest
// date would mask older evidence), plus whether any positive contributor
// carries no evidence date at all. A zero-count housing contributes nothing
// and cannot affect freshness. This is disclosure data; it must never be
// used to extrapolate a "current" number (additions, transfers, or losses
// may all be unreported).
export function layersFreshnessAt(
  layerBatches: readonly LayerBatchRow[] | null | undefined,
  layerHousings: readonly LayerHousingRow[] | null | undefined,
  layerDailys: readonly DailyReportRow[] | null | undefined,
  asOfDate: AnimalHistoryDateInput,
): {oldestReported: string | null; hasUndatedCounts: boolean} {
  const positives = layerEvidenceEntries(layerBatches, layerHousings, layerDailys, asOfDate).filter((e) => e.count > 0);
  const oldestReported = positives.reduce<string | null>(
    (oldest, e) => (e.date && (!oldest || e.date < oldest) ? e.date : oldest),
    null,
  );
  return {
    oldestReported,
    hasUndatedCounts: positives.some((e) => !e.date),
  };
}

function feederTargets(feederGroups: readonly PigFeederGroupRow[] | null | undefined): FeederTarget[] {
  const targets: FeederTarget[] = [];
  for (const group of feederGroups || []) {
    if (!group) continue;
    const subs = Array.isArray(group.subBatches) ? group.subBatches : [];
    if (subs.length > 0) {
      for (const sub of subs) {
        if (!sub) continue;
        targets.push({
          group,
          sub,
          subId: sub.id || null,
          label: sub.name || group.batchName,
          start: isoDate(sub.startDate || group.startDate),
          status: sub.status || group.status,
          started: int(sub.giltCount) + int(sub.boarCount) || int(sub.originalPigCount),
        });
      }
      continue;
    }
    targets.push({
      group,
      sub: null,
      subId: null,
      label: group.batchName,
      start: isoDate(group.startDate),
      status: group.status,
      started: int(group.giltCount) + int(group.boarCount) || (group.farmBorn ? int(group.originalPigCount) : 0),
    });
  }
  return targets;
}

function pigDailyMatchesLabel(daily: DailyReportRow | null | undefined, label: string | null | undefined): boolean {
  if (!daily || !label) return false;
  const keys = new Set([normLabel(label), pigSlug(label)].filter(Boolean));
  const labelKey = normLabel(daily.batch_label);
  if (keys.has(labelKey) || keys.has(pigSlug(labelKey))) return true;
  const idKey = normLabel(daily.batch_id);
  return keys.has(idKey) || keys.has(pigSlug(idKey));
}

function latestPigDailyCount(
  pigDailys: readonly DailyReportRow[] | null | undefined,
  label: string | null | undefined,
  asOf: string,
): {count: number; date: string | null} | null {
  const rows = (pigDailys || [])
    .filter((d) => {
      const date = isoDate(d && d.date);
      return date && date <= asOf && pigDailyMatchesLabel(d, label) && int(d.pig_count) > 0;
    })
    .sort((a, b) => (isoDate(b.date) || '').localeCompare(isoDate(a.date) || ''));
  const row = rows[0] || null;
  return row ? {count: int(row.pig_count), date: isoDate(row.date)} : null;
}

function targetTripCount(target: FeederTarget, asOf: string, afterDate: string | null = null): number {
  let count = 0;
  for (const trip of (target.group && target.group.processingTrips) || []) {
    const date = isoDate(trip && trip.date);
    if (!date || date > asOf || (afterDate && date <= afterDate)) continue;
    if (!target.subId) {
      count += int(trip.pigCount);
      continue;
    }
    const attributions = Array.isArray(trip.subAttributions) ? trip.subAttributions : [];
    if (attributions.length > 0) {
      for (const att of attributions) {
        if (!att) continue;
        if (att.subId === target.subId || normLabel(att.subBatchName) === normLabel(target.label))
          count += int(att.count);
      }
    } else if (((target.group && target.group.subBatches) || []).length === 1) {
      count += int(trip.pigCount);
    }
  }
  return count;
}

function targetMortalityCount(target: FeederTarget, asOf: string, afterDate: string | null = null): number {
  let count = 0;
  for (const entry of (target.group && target.group.pigMortalities) || []) {
    const date = isoDate(entry && (entry.date || entry.created_at));
    if (!date || date > asOf || (afterDate && date <= afterDate)) continue;
    if (target.subId) {
      if (entry.sub_batch_id === target.subId || normLabel(entry.sub_batch_name) === normLabel(target.label))
        count += int(entry.count);
    } else {
      count += int(entry.count);
    }
  }
  return count;
}

function targetTransferCount(
  target: FeederTarget,
  breeders: readonly PigBreederRow[] | null | undefined,
  asOf: string,
  afterDate: string | null = null,
): number {
  let count = 0;
  for (const breeder of breeders || []) {
    const source = breeder && breeder.transferredFromBatch;
    if (!source || source.batchName !== (target.group && target.group.batchName)) continue;
    if (target.subId && normLabel(source.subBatchName) !== normLabel(target.label)) continue;
    const date = isoDate(source.transferDate || source.created_at);
    if (!date || date > asOf || (afterDate && date <= afterDate)) continue;
    count += 1;
  }
  return count;
}

// Feeder statuses that mean "this batch is no longer on the farm". Derived from
// the two sources of truth rather than invented: PROD `ppp-feeders-v1` carries
// exactly `active` / `processed` / `archived` (archived is legacy — the current
// PigBatchesView UI only ever writes `active` or `processed`), and the repo's
// existing outcome vocabulary for breeders is OUTCOME_GROUPS above
// (processed / deceased / sold). This set is their union.
//
// It is deliberately a BLOCKLIST and not an `=== 'active'` allowlist. An unknown
// or missing status therefore falls through to the evidence path below and is
// still counted. That is the fail-safe direction: a blocklist's failure mode is
// showing a stale animal, which is visible and reportable; an allowlist's
// failure mode is silently hiding a live one.
const TERMINAL_FEEDER_STATUSES = new Set(['processed', 'archived', 'sold', 'deceased']);

// A sub-batch is off the farm when EITHER it or its parent group is terminal.
// That mirrors /pig (PigsHomeView), which requires the group AND the sub to be
// 'active' before it counts a label, so the two surfaces cannot disagree about
// which batches exist — only about how many are in them.
function targetIsTerminal(target: FeederTarget): boolean {
  const groupStatus = norm(target.group && target.group.status);
  const subStatus = norm(target.sub && target.sub.status);
  return TERMINAL_FEEDER_STATUSES.has(groupStatus) || TERMINAL_FEEDER_STATUSES.has(subStatus);
}

// The last processing trip that can be attributed to this target, falling back
// to the group's last trip of any kind. The attribution test is the same one
// targetTripCount uses. The fallback matters: P-26-01's four trips carry no
// subAttributions at all, so its two sub-batches have no attributable trip even
// though the trips are the only record that the batch was worked through.
// Deliberately NOT bounded by asOf — the end of a batch is a property of the
// batch, not of the window being asked about; the asOf comparison happens at
// the call site.
function lastAttributableTripDate(target: FeederTarget): string | null {
  let attributed: string | null = null;
  let anyTrip: string | null = null;
  for (const trip of (target.group && target.group.processingTrips) || []) {
    const date = isoDate(trip && trip.date);
    if (!date) continue;
    if (!anyTrip || date > anyTrip) anyTrip = date;
    let hits: boolean;
    if (!target.subId) {
      hits = true;
    } else {
      const attributions = Array.isArray(trip.subAttributions) ? trip.subAttributions : [];
      hits =
        attributions.length > 0
          ? attributions.some(
              (att) => !!att && (att.subId === target.subId || normLabel(att.subBatchName) === normLabel(target.label)),
            )
          : ((target.group && target.group.subBatches) || []).length === 1;
    }
    if (hits && (!attributed || date > attributed)) attributed = date;
  }
  return attributed || anyTrip;
}

function lastPigDailyDate(
  pigDailys: readonly DailyReportRow[] | null | undefined,
  label: string | null | undefined,
): string | null {
  return maxISO(
    (pigDailys || [])
      .filter((d) => pigDailyMatchesLabel(d, label) && int(d.pig_count) > 0)
      .map((d) => isoDate(d && d.date)),
  );
}

// The last date a terminal feeder target has any evidence of being on the farm.
//
// Feeder groups have no end-date field — verified against PROD, where every
// group carries only batchName / startDate / status / processingTrips /
// subBatches and friends. So unlike broilersOnFarmAt, which reads
// batch.processingDate straight off the row, this has to be derived.
//
// It is the LATEST of the trip and daily evidence, not the last trip alone.
// Pigs sometimes leave without a processing trip (private sale, transfer to
// breeding stock), and dailys that continue past the last trip are direct proof
// the batch was still here. Taking the max can only ever keep a batch visible
// LONGER, never retire one early — the safe direction for an on-farm count,
// since hiding a live animal is the worse error.
//
// Note this is a date of PRESENCE, not of departure — which is why the caller
// compares with `<` and not broilers' `<=`. A batch whose last daily counted 18
// pigs must still report 18 on that date; retiring it on its own last day of
// evidence would contradict the record. On the end date itself the ordinary
// evidence path runs and nets out any same-day trip or transfer on its own.
function terminalTargetEndDate(
  target: FeederTarget,
  pigDailys: readonly DailyReportRow[] | null | undefined,
): string | null {
  return maxISO([lastAttributableTripDate(target), lastPigDailyDate(pigDailys, target.label)]);
}

function feederTargetCountAt(
  target: FeederTarget,
  breeders: readonly PigBreederRow[] | null | undefined,
  pigDailys: readonly DailyReportRow[] | null | undefined,
  asOf: string,
): number {
  if (!target || !target.label || !target.start || target.start > asOf) return 0;

  // Off-farm exclusion: date first, status only as the fallback. This is the
  // same shape broilersOnFarmAt uses for batch.processingDate and
  // layerEvidenceEntries uses for housing.retired_date, and it is why this is
  // not simply `status === 'active'`.
  //
  // Filtering on CURRENT status alone would fix today and corrupt the past:
  // pigsOnFarmAt also drives the "animals on farm at date X" history page, and
  // P-24-01 genuinely WAS on the farm in early 2025. Excluding a terminal batch
  // only from its derived end date onward keeps both answers right, with no
  // special case for "is asOf today".
  //
  // Before this guard, the only status test in this function was a ternary whose
  // two branches both returned 0 — a no-op that read as if a status filter
  // existed. Home's "Animals on Farm" tile therefore carried every stale batch
  // forever: four processed/archived batches were still contributing their last
  // daily count, reporting 92 pigs against a true 63.
  if (targetIsTerminal(target)) {
    const end = terminalTargetEndDate(target, pigDailys);
    // No derivable end date at all — no trips, no dailys — leaves nothing but
    // the status to go on, so the batch is excluded at every asOf. That is the
    // pure broiler fallback (`if (!processed && status === 'processed')`), and
    // it is the honest option: with zero dated evidence there is no defensible
    // date at which to place the batch on the farm.
    if (!end || end < asOf) return 0;
  }

  const latest = latestPigDailyCount(pigDailys, target.label, asOf);
  if (latest) {
    const laterEvents =
      targetTripCount(target, asOf, latest.date) +
      targetMortalityCount(target, asOf, latest.date) +
      targetTransferCount(target, breeders, asOf, latest.date);
    return Math.max(0, latest.count - laterEvents);
  }
  // No dated evidence and no starting count: nothing to report either way. This
  // was a ternary whose branches both returned 0; the status decision it looked
  // like it was making now lives in the terminal/end-date guard above.
  if (target.started <= 0) return 0;
  return Math.max(
    0,
    target.started -
      targetTripCount(target, asOf) -
      targetMortalityCount(target, asOf) -
      targetTransferCount(target, breeders, asOf),
  );
}

function breederStartDate(breeder: PigBreederRow): string | null {
  const transferDate = breeder && breeder.transferredFromBatch && breeder.transferredFromBatch.transferDate;
  return isoDate(
    transferDate || breeder.purchaseDate || breeder.purchase_date || breeder.created_at || breeder.birthDate,
  );
}

function breederIsActiveAt(breeder: PigBreederRow | null | undefined, asOf: string): boolean {
  if (!breeder) return false;
  const status = norm(breeder.status);
  if (breeder.archived || OUTCOME_GROUPS.has(status)) return false;
  const start = breederStartDate(breeder);
  return !start || start <= asOf;
}

function breederFallbackCount(
  breeders: readonly PigBreederRow[] | null | undefined,
  asOf: string,
  sexGroup: 'sows' | 'boars',
): number {
  return (breeders || []).filter((b) => {
    if (!breederIsActiveAt(b, asOf)) return false;
    const sex = norm(b.sex);
    return sexGroup === 'boars' ? sex === 'boar' : sex === 'sow' || sex === 'gilt';
  }).length;
}

export function pigsOnFarmAt(
  feederGroups: readonly PigFeederGroupRow[] | null | undefined,
  breeders: readonly PigBreederRow[] | null | undefined,
  pigDailys: readonly DailyReportRow[] | null | undefined,
  asOfDate: AnimalHistoryDateInput,
): number {
  const asOf = isoDate(asOfDate);
  if (!asOf) return 0;
  const feederTotal = feederTargets(feederGroups).reduce(
    (sum, target) => sum + feederTargetCountAt(target, breeders, pigDailys, asOf),
    0,
  );
  const sowsDaily = latestPigDailyCount(pigDailys, 'SOWS', asOf);
  const boarsDaily = latestPigDailyCount(pigDailys, 'BOARS', asOf);
  const sows = sowsDaily ? sowsDaily.count : breederFallbackCount(breeders, asOf, 'sows');
  const boars = boarsDaily ? boarsDaily.count : breederFallbackCount(breeders, asOf, 'boars');
  return feederTotal + sows + boars;
}

function animalStartDate(
  animal: HerdAnimalRow | null | undefined,
  transfers: readonly HerdTransferRow[] | null | undefined,
  groupField: 'herd' | 'flock',
): string | null {
  return minISO([
    animal && (animal.purchase_date || animal.purchaseDate),
    animal && animal.created_at,
    ...(transfers || []).map((t) => t && (t.transferred_at || t.created_at)),
    animal && animal.birth_date,
    animal && animal.birthDate,
    animal && animal[groupField] ? new Date().toISOString().slice(0, 10) : null,
  ]);
}

function animalGroupAt(
  animal: HerdAnimalRow | null | undefined,
  transfers: readonly HerdTransferRow[] | null | undefined,
  asOf: string,
  groupField: 'herd' | 'flock',
  fromField: 'from_herd' | 'from_flock',
): string {
  let group = norm(animal && animal[groupField]);
  const later = [...(transfers || [])].sort((a, b) =>
    (isoDate(b.transferred_at || b.created_at) || '').localeCompare(isoDate(a.transferred_at || a.created_at) || ''),
  );
  for (const transfer of later) {
    const date = isoDate(transfer && (transfer.transferred_at || transfer.created_at));
    if (date && date > asOf) group = norm(transfer[fromField]) || group;
  }
  return group;
}

function animalIsAliveAt(animal: HerdAnimalRow | null | undefined, asOf: string): boolean {
  const sale = isoDate(animal && (animal.sale_date || animal.sold_date));
  const death = isoDate(animal && animal.death_date);
  if (sale && sale <= asOf) return false;
  if (death && death <= asOf) return false;
  return true;
}

function herdCountAt(
  rows: readonly HerdAnimalRow[] | null | undefined,
  transfers: readonly HerdTransferRow[] | null | undefined,
  asOfDate: AnimalHistoryDateInput,
  {
    idField,
    groupField,
    fromField,
    activeSet,
  }: {
    idField: 'cattle_id' | 'sheep_id';
    groupField: 'herd' | 'flock';
    fromField: 'from_herd' | 'from_flock';
    activeSet: ReadonlySet<string>;
  },
): number {
  const asOf = isoDate(asOfDate);
  if (!asOf) return 0;
  const transfersById = groupById(transfers, idField);
  let count = 0;
  for (const row of rows || []) {
    if (!row || row.deleted_at) continue;
    const rowTransfers = (row.id != null ? transfersById.get(row.id) : undefined) || [];
    const start = animalStartDate(row, rowTransfers, groupField);
    if (start && start > asOf) continue;
    if (!animalIsAliveAt(row, asOf)) continue;
    const group = animalGroupAt(row, rowTransfers, asOf, groupField, fromField);
    if (activeSet.has(group)) count += 1;
  }
  return count;
}

export function cattleOnFarmAt(
  cattle: readonly HerdAnimalRow[] | null | undefined,
  cattleTransfers: readonly HerdTransferRow[] | null | undefined,
  asOfDate: AnimalHistoryDateInput,
): number {
  return herdCountAt(cattle, cattleTransfers, asOfDate, {
    idField: 'cattle_id',
    groupField: 'herd',
    fromField: 'from_herd',
    activeSet: ACTIVE_CATTLE_SET,
  });
}

export function sheepOnFarmAt(
  sheep: readonly HerdAnimalRow[] | null | undefined,
  sheepTransfers: readonly HerdTransferRow[] | null | undefined,
  asOfDate: AnimalHistoryDateInput,
): number {
  return herdCountAt(sheep, sheepTransfers, asOfDate, {
    idField: 'sheep_id',
    groupField: 'flock',
    fromField: 'from_flock',
    activeSet: ACTIVE_SHEEP_SET,
  });
}

export function inferAnimalHistoryStartMonth(data: AnimalHistoryData = {}): string | null {
  const dates: AnimalHistoryDateInput[] = [];
  for (const b of data.batches || []) dates.push(b.hatchDate, b.hatch_date, b.processingDate, b.processing_date);
  for (const d of data.broilerDailys || []) dates.push(d.date);
  for (const b of data.layerBatches || []) dates.push(b.arrival_date, b.brooder_entry_date, b.start_date);
  for (const h of data.layerHousings || []) dates.push(h.start_date, h.retired_date, h.current_count_date);
  for (const d of data.layerDailys || []) dates.push(d.date);
  for (const g of data.feederGroups || []) {
    dates.push(g.startDate);
    for (const s of g.subBatches || []) dates.push(s.startDate || g.startDate);
    for (const t of g.processingTrips || []) dates.push(t.date);
    for (const m of g.pigMortalities || []) dates.push(m.date || m.created_at);
  }
  for (const b of data.breeders || []) {
    if (b && b.transferredFromBatch) dates.push(b.transferredFromBatch.transferDate);
  }
  for (const d of data.pigDailys || []) dates.push(d.date);
  for (const c of data.cattle || [])
    dates.push(c.purchase_date, c.purchaseDate, c.created_at, c.sale_date, c.sold_date, c.death_date);
  for (const t of data.cattleTransfers || []) dates.push(t.transferred_at, t.created_at);
  for (const s of data.sheep || [])
    dates.push(s.purchase_date, s.purchaseDate, s.created_at, s.sale_date, s.sold_date, s.death_date);
  for (const t of data.sheepTransfers || []) dates.push(t.transferred_at, t.created_at);

  const earliest = minISO(dates);
  return earliest ? earliest.slice(0, 7) : null;
}

export function buildAnimalHistoryRows(
  data: AnimalHistoryData = {},
  asOfDate: AnimalHistoryDateInput = new Date(),
): AnimalHistorySnapshot[] {
  const today = isoDate(asOfDate) || new Date().toISOString().slice(0, 10);
  const inferredStartMonth = inferAnimalHistoryStartMonth(data);
  const endMonth = monthKey(today);
  if (!inferredStartMonth || !endMonth) return [];
  const startMonth =
    inferredStartMonth && inferredStartMonth > ANIMAL_HISTORY_START_MONTH
      ? inferredStartMonth
      : ANIMAL_HISTORY_START_MONTH;
  return monthsBetween(startMonth, endMonth)
    .map((month) => {
      const snapshotDate = snapshotDateForMonth(month, today);
      return buildAnimalHistorySnapshot(data, snapshotDate);
    })
    .reverse();
}

export function buildAnimalHistorySnapshot(
  data: AnimalHistoryData = {},
  asOfDate: AnimalHistoryDateInput = new Date(),
): AnimalHistorySnapshot {
  const snapshotDate = isoDate(asOfDate) || new Date().toISOString().slice(0, 10);
  const month = monthKey(snapshotDate);
  const end = monthEnd(month);
  // No combined total: species counts stay separate on every surface.
  // Counts are "latest recorded" evidence at the snapshot date, not verified
  // live inventory; layersOldestReported / layersHasUndatedCounts carry the
  // layer freshness disclosure (oldest contributing evidence date + whether
  // any positive contributor is undated).
  const layersFreshness = layersFreshnessAt(data.layerBatches, data.layerHousings, data.layerDailys, snapshotDate);
  const broilers = broilersOnFarmDetailAt(data.batches, data.broilerDailys, snapshotDate);
  return {
    month,
    snapshotDate,
    isPartialMonth: !!end && snapshotDate < end,
    broilers: broilers.count,
    broilersEstimated: broilers.estimated,
    layers: layersOnFarmAt(data.layerBatches, data.layerHousings, data.layerDailys, snapshotDate),
    layersOldestReported: layersFreshness.oldestReported,
    layersHasUndatedCounts: layersFreshness.hasUndatedCounts,
    pigs: pigsOnFarmAt(data.feederGroups, data.breeders, data.pigDailys, snapshotDate),
    cattle: cattleOnFarmAt(data.cattle, data.cattleTransfers, snapshotDate),
    sheep: sheepOnFarmAt(data.sheep, data.sheepTransfers, snapshotDate),
  };
}

// Per-species chart y-axis maximum. Each species chart computes its own scale
// from its own values only, so one large flock cannot flatten another
// species' trend line.
export function animalHistoryScaleMax(values: readonly unknown[] | null | undefined): number {
  const rawMax = Math.max(1, ...(values || []).map((v) => num(v)).filter((v) => Number.isFinite(v)));
  const stepBase = rawMax > 1000 ? 500 : rawMax > 250 ? 100 : rawMax > 100 ? 50 : rawMax > 50 ? 25 : 10;
  return Math.max(stepBase, Math.ceil(rawMax / stepBase) * stepBase);
}

function formatUtcDate(value: string | null | undefined, options: Intl.DateTimeFormatOptions): string {
  const match = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(String(value || ''));
  if (!match) return value || '';
  const year = parseInt(match[1], 10);
  const monthNum = parseInt(match[2], 10);
  const day = parseInt(match[3] || '1', 10);
  return new Date(Date.UTC(year, monthNum - 1, day)).toLocaleDateString('en-US', {
    ...options,
    timeZone: 'UTC',
  });
}

export function formatAnimalHistoryMonth(month: string | null | undefined): string {
  if (!/^\d{4}-\d{2}$/.test(String(month || ''))) return month || '';
  return formatUtcDate(month, {month: 'short', year: 'numeric'});
}

export const _animalHistoryInternals = {
  isoDate,
  monthEnd,
  monthsBetween,
  snapshotDateForMonth,
  feederTargets,
};
